import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, ChevronDown, MapPin, MessageCircle, Phone } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { LedTicker } from "../components/LedTicker";
import { SITE } from "../data/site";
import { PRODUCTS } from "../data/products";
import { CITIES, getCity } from "../data/cities";

const FaqItem = ({ faq, testid }) => (
    <details
        data-testid={testid}
        className="group rounded-xl border border-slate-800 bg-[#12141E] px-5 py-4 open:border-[#FF9900]/40"
    >
        <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-medium text-slate-100 [&::-webkit-details-marker]:hidden">
            {faq.q}
            <ChevronDown className="h-5 w-5 shrink-0 text-slate-500 transition-transform group-open:rotate-180" aria-hidden="true" />
        </summary>
        <p className="mt-3 text-sm leading-relaxed text-slate-400">{faq.a}</p>
    </details>
);

const FEATURED_PRODUCTS = [
    "scrolling-led-boards",
    "led-video-wall",
    "digital-signage",
    "interactive-flat-panel-displays",
    "sound-systems",
    "writing-display-boards",
];

export default function CityPage() {
    const { slug } = useParams();
    const city = getCity(slug);

    if (!city) {
        return (
            <div className="mx-auto max-w-3xl px-4 py-32 text-center" data-testid="city-not-found">
                <h1 className="font-display text-4xl font-bold uppercase">Page not found</h1>
                <Link to="/" data-testid="city-notfound-home" className="mt-6 inline-flex items-center gap-2 font-semibold text-[#FF9900]">
                    Back to home <ArrowRight className="h-4 w-4" />
                </Link>
            </div>
        );
    }

    const cityLabel = city.altName ? `${city.name} (${city.altName})` : city.name;
    const phoneDisplay = `+91 ${city.phone.slice(0, 5)} ${city.phone.slice(5)}`;
    const phoneTel = `tel:+91${city.phone}`;
    const path = `/led-display-boards/${city.slug}`;
    const url = `${SITE.domain}${path}`;
    const featured = FEATURED_PRODUCTS.map((s) => PRODUCTS.find((p) => p.slug === s)).filter(Boolean);

    const jsonLd = [
        {
            "@context": "https://schema.org",
            "@type": "Service",
            name: `Scrolling LED Boards & Display Solutions in ${cityLabel}`,
            description: city.metaDescription,
            url,
            provider: {
                "@type": "LocalBusiness",
                name: "Morning Star Enterprises",
                url: SITE.domain + "/",
                telephone: `+91${city.phone}`,
                address: {
                    "@type": "PostalAddress",
                    streetAddress: "Vylassery Road, Palarivattom",
                    addressLocality: "Kochi",
                    addressRegion: "Kerala",
                    postalCode: "682025",
                    addressCountry: "IN",
                },
            },
            areaServed: { "@type": "City", name: city.name },
        },
        {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE.domain + "/" },
                { "@type": "ListItem", position: 2, name: `LED Boards ${city.name}`, item: url },
            ],
        },
        {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: city.faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
        },
    ];

    return (
        <>
            <Seo
                title={city.metaTitle}
                description={city.metaDescription}
                keywords={city.keywords}
                path={path}
                jsonLd={jsonLd}
            />

            <section className="led-dots spotlight border-b border-slate-800/70" data-testid="city-hero">
                <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
                    <nav aria-label="Breadcrumb" className="text-sm text-slate-500">
                        <ol className="flex flex-wrap items-center gap-2">
                            <li>
                                <Link to="/" data-testid="city-breadcrumb-home" className="transition-colors hover:text-[#FF9900]">Home</Link>
                            </li>
                            <li aria-hidden="true">/</li>
                            <li className="text-slate-300" aria-current="page">LED Boards {city.name}</li>
                        </ol>
                    </nav>
                    <p className="mt-6 flex items-center gap-2 font-led text-lg uppercase tracking-[0.35em] text-[#00A8FF]">
                        <MapPin className="h-4 w-4" aria-hidden="true" /> {cityLabel}, Kerala
                    </p>
                    <div className="mt-3 overflow-hidden">
                        <motion.h1
                            initial={{ y: "105%" }}
                            animate={{ y: "0%" }}
                            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                            className="max-w-4xl font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl"
                        >
                            Scrolling LED Boards &amp; Displays in <span className="led-glow-amber">{city.name}</span>
                        </motion.h1>
                    </div>
                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.35, duration: 0.7 }}
                        className="mt-5 max-w-2xl text-base leading-relaxed text-slate-400 md:text-lg"
                    >
                        {city.intro[0]}
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5, duration: 0.7 }}
                        className="mt-8 flex flex-wrap gap-4"
                    >
                        <a
                            href={SITE.whatsappFor(`LED boards in ${city.name}`)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="city-whatsapp-cta"
                            className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 font-semibold text-black shadow-[0_0_18px_rgba(37,211,102,0.45)] transition-transform hover:scale-105"
                        >
                            <MessageCircle className="h-5 w-5" /> Get a {city.name} Quote
                        </a>
                        <a
                            href={phoneTel}
                            data-testid="city-call-cta"
                            className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-6 py-3 font-semibold text-slate-200 transition-colors hover:border-[#FF9900] hover:text-[#FF9900]"
                        >
                            <Phone className="h-5 w-5" /> Call {city.name}: {phoneDisplay}
                        </a>
                    </motion.div>
                </div>
                <LedTicker
                    messages={[
                        `Scrolling LED boards in ${city.name}`,
                        `LED video walls in ${city.name}`,
                        `Digital signage in ${city.name}`,
                        `WhatsApp ${SITE.phoneDisplay}`,
                    ]}
                />
            </section>

            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6" data-testid="city-overview">
                <div className="grid gap-10 lg:grid-cols-5">
                    <Reveal className="lg:col-span-3">
                        <h2 className="font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl">
                            LED display specialists serving {cityLabel}
                        </h2>
                        {city.intro.slice(1).map((p, i) => (
                            <p key={i} className="mt-4 text-base leading-relaxed text-slate-400">{p}</p>
                        ))}
                        <h3 className="mt-8 text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">
                            Areas covered in &amp; around {city.name}
                        </h3>
                        <div className="mt-4 flex flex-wrap gap-2">
                            {city.areas.map((a) => (
                                <span key={a} className="rounded-full border border-slate-800 bg-[#12141E] px-3 py-1.5 text-xs text-slate-400">
                                    {a}
                                </span>
                            ))}
                        </div>
                    </Reveal>
                    <Reveal delay={0.12} className="lg:col-span-2">
                        <div className="rounded-2xl border border-slate-800 bg-[#12141E] p-6">
                            <h3 className="font-display text-xl font-semibold uppercase tracking-wide">
                                Why {city.name} chooses Morningstar
                            </h3>
                            <ul className="mt-4 space-y-3">
                                {city.highlights.map((h) => (
                                    <li key={h} className="flex gap-3 text-sm text-slate-300">
                                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#FF9900]" aria-hidden="true" />
                                        {h}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="border-y border-slate-800/70 bg-[#0C0E16]" data-testid="city-products">
                <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
                    <Reveal>
                        <h2 className="font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl">
                            What we install in {city.name}
                        </h2>
                    </Reveal>
                    <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {featured.map((p, i) => (
                            <Reveal key={p.slug} delay={(i % 3) * 0.08}>
                                <Link
                                    to={`/products/${p.slug}`}
                                    data-testid={`city-product-${p.slug}`}
                                    className="group flex h-full flex-col justify-between rounded-2xl border border-slate-800 bg-[#12141E] p-6 transition-colors hover:border-[#FF9900]/40"
                                >
                                    <div>
                                        <h3 className="font-display text-xl font-semibold uppercase tracking-wide text-slate-100">
                                            {p.name}
                                        </h3>
                                        <p className="mt-2 line-clamp-2 text-sm text-slate-400">{p.tagline}</p>
                                    </div>
                                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#FF9900]">
                                        View details
                                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                                    </span>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="mx-auto max-w-4xl px-4 py-16 sm:px-6" data-testid="city-faq">
                <Reveal>
                    <h2 className="font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl">
                        {city.name} — frequently asked questions
                    </h2>
                </Reveal>
                <div className="mt-8 space-y-3">
                    {city.faqs.map((f, i) => (
                        <Reveal key={f.q} delay={i * 0.05}>
                            <FaqItem faq={f} testid={`city-faq-${i}`} />
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="border-t border-slate-800/70 bg-[#0C0E16]" data-testid="city-other-cities">
                <div className="mx-auto max-w-7xl px-4 py-16 text-center sm:px-6">
                    <Reveal>
                        <h2 className="font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl">
                            Planning a display in {city.name}?{" "}
                            <span className="led-glow-amber">Let's talk.</span>
                        </h2>
                        <a
                            href={SITE.whatsappFor(`LED boards in ${city.name}`)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="city-bottom-whatsapp"
                            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 font-semibold text-black shadow-[0_0_18px_rgba(37,211,102,0.45)] transition-transform hover:scale-105"
                        >
                            <MessageCircle className="h-5 w-5" /> Chat on WhatsApp — {SITE.phoneDisplay}
                        </a>
                        <p className="mt-6 text-sm text-slate-400">
                            Call our {city.name} line:{" "}
                            <a href={phoneTel} data-testid="city-phone-text" className="font-semibold text-[#FF9900]">
                                {phoneDisplay}
                            </a>{" "}
                            · WhatsApp (all locations): {SITE.phoneDisplay}
                        </p>
                        <p className="mt-10 text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">
                            We also serve
                        </p>
                        <div className="mt-4 flex flex-wrap justify-center gap-3">
                            {CITIES.filter((c) => c.slug !== city.slug).map((c) => (
                                <Link
                                    key={c.slug}
                                    to={`/led-display-boards/${c.slug}`}
                                    data-testid={`city-link-${c.slug}`}
                                    className="rounded-full border border-slate-700 px-4 py-2 text-sm text-slate-300 transition-colors hover:border-[#FF9900] hover:text-[#FF9900]"
                                >
                                    LED Boards {c.name}
                                </Link>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
